import api from './api';

export interface ChequeCartera {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number;
  nroDestino: number;
  fechaSalida: string;
  nroMovCajaSalida: number;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
}

export interface GrupoCajaChica {
  id: number;
  nombre: string;
  codigo: number;
}

export interface Caja {
  nroCaja?: number;
  codigo?: number;
  grupo?: number;
  estado?: string;
  [key: string]: any;
}

export interface GrupoMcpDetalle {
  grupoMcp: number;
  descripcion: string;
}

export interface MedioTransferencia {
  id: number;
  nombre: string; // opcional en UI, mantenemos por compatibilidad
  descripcion?: string; // API: descripcion
  mcp?: string;
  clase?: string;
  // Campos adicionales para derivaciones de payload
  grupo?: number;
  nroSuc?: number;
  tipo?: string;
}

export interface MarcaTarjeta {
  id: number;
  nombre: string;
  nombreMarca?: string;
}

export interface Banco {
  id: number;
  nombre: string;
  codigo?: number | string;
  nroBanco?: number | string;
}
export interface GetGruposCajaOptions {
  usuario?: string;
  nroCliente?: number;
}
export interface GetGruposCajaPagedOptions extends GetGruposCajaOptions {
  pageNumber: number;
  pageSize: number;
}

const tesoreriaService = {
  getGruposCajaChica: async (options?: GetGruposCajaOptions): Promise<GrupoCajaChica[]> => {
    const token = localStorage.getItem('token');
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 50, // Límite del backend para GrupoMcpUsuario
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Eliminar duplicados por id/código
    const uniqueById = new Map<number, GrupoCajaChica>();
    for (const item of mapped) {
      if (!uniqueById.has(item.id)) {
        uniqueById.set(item.id, item);
      }
    }

    // Enriquecer con descripción real de cada grupo
    const gruposUnicos = Array.from(uniqueById.keys());
    try {
      const detalles = await Promise.all(
        gruposUnicos.map(async (gid) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${gid}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );

      for (const det of detalles) {
        const current = uniqueById.get(det.grupoMcp);
        if (current) {
          uniqueById.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
          });
        }
      }
    } catch (err) {
      // Si falla la descripción, devolvemos los nombres por defecto
      console.warn('No se pudieron cargar descripciones de Grupo MCP', err);
    }

    return Array.from(uniqueById.values());
  },

  getGruposCajaChicaPaged: async (options: GetGruposCajaPagedOptions): Promise<GrupoCajaChica[]> => {
    const token = localStorage.getItem('token');
    const storedUser =
      options?.usuario ||
      localStorage.getItem('userName') ||
      localStorage.getItem('username') ||
      localStorage.getItem('user') ||
      undefined;
    const response = await api.get<any>('/api/GrupoMcpUsuario', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        ...(options?.nroCliente ? { nroCliente: options.nroCliente } : {}),
        ...(storedUser ? { usuario: storedUser, Usuario: storedUser } : {}),
      },
    });
    const data = response.data;

    const rawItems: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

    const mapped: GrupoCajaChica[] = rawItems.map((it: any) => {
      const grupo = it?.grupoMcp ?? it?.grupo ?? it?.id ?? 0;
      return {
        id: grupo,
        nombre: `Grupo MCP ${grupo}`,
        codigo: grupo,
      };
    });

    // Enriquecer con descripción real de cada grupo (solo de esta página)
    try {
      const detalles = await Promise.all(
        mapped.map(async (g) => {
          const detResp = await api.get<GrupoMcpDetalle>(`/api/GrupoMcp/${g.id}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
            },
          });
          return detResp.data;
        })
      );
      const byId = new Map<number, GrupoCajaChica>(mapped.map(m => [m.id, m]));
      for (const det of detalles) {
        const current = byId.get(det.grupoMcp);
        if (current) {
          byId.set(det.grupoMcp, {
            ...current,
            nombre: det.descripcion || current.nombre,
          });
        }
      }
      return Array.from(byId.values());
    } catch (err) {
      console.warn('No se pudieron cargar descripciones de Grupo MCP (paginado)', err);
      return mapped;
    }
  },

  /** Obtiene TODOS los MCPs por tipo desde GET /api/Mcps?Tipo=X, paginando todas las páginas. */
  getMcpsByTipo: async (tipo: string): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipo ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];
    const token = localStorage.getItem('token');
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 1000;
    let globalIdx = 0;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { Tipo: tipoNorm, PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      allItems.push(...items.map((it) => mapItem(it, globalIdx++)));
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras desde GET /api/Mcps (todas las páginas). */
  getMcpsAll: async (): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const mapItem = (it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    });
    const allItems: MedioTransferencia[] = [];
    let pageNumber = 1;
    const pageSize = 1000;
    for (let i = 0; i < 200; i++) {
      const response = await api.get<any>('/api/Mcps', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: { PageNumber: pageNumber, PageSize: pageSize },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
      if (items.length === 0) break;
      allItems.push(...items.map(mapItem));
      if (items.length < pageSize) break;
      pageNumber += 1;
    }
    return allItems;
  },

  /** Obtiene TODAS las chequeras de todos los grupos. Intenta GET /api/Mcps; si falla, usa por-grupo. */
  getMcpsPorTipo: async (tipoBuscado: string, grupoActual?: number): Promise<MedioTransferencia[]> => {
    const tipoNorm = (tipoBuscado ?? '').toString().trim().toUpperCase();
    if (!tipoNorm) return [];

    let todos: MedioTransferencia[] = await tesoreriaService.getMcpsAll().catch(() => []);
    if (todos.length === 0) {
      const gruposList = await tesoreriaService.getGruposCajaChica().catch(() => []);
      const grupos = gruposList.length > 0 ? gruposList : (grupoActual != null ? [{ id: grupoActual, nombre: '', codigo: grupoActual }] : []);
      if (grupos.length > 0) {
        const results = await Promise.all(grupos.map((g) => tesoreriaService.getMcpsPorGrupo(g.codigo).catch(() => [] as MedioTransferencia[])));
        todos = results.flat();
      }
    }
    const filtrados = todos.filter((m) => {
      const mTipo = (m.tipo ?? '').toString().trim().toUpperCase();
      const mClase = (m.clase ?? '').toString().trim().toUpperCase();
      const mDesc = (m.descripcion ?? m.nombre ?? '').toString().toUpperCase();
      const mMcp = (m.mcp ?? '').toString().toUpperCase();
      const mFirst = mTipo.charAt(0);
      if (tipoNorm === 'E') {
        if (mFirst === 'P') return false;
        return mTipo === 'E' || mFirst === 'E' || mClase.includes('EFECTIVO') || mDesc.includes('EFECTIVO') || mMcp.includes('EFECTIVO');
      }
      if (tipoNorm === 'P' || tipoNorm === 'C') {
        if (mFirst === 'E') return false;
        return mTipo === tipoNorm || mFirst === tipoNorm || mClase.includes('CHEQUE') || mDesc.includes('CHEQUE') || mMcp.includes('CHEQUE');
      }
      if (tipoNorm === 'T') {
        return mFirst === 'T' || mClase.includes('TARJETA') || mDesc.includes('TARJ') || mMcp.includes('TARJ');
      }
      return mTipo === tipoNorm || mFirst === tipoNorm;
    });
    const byMcp = new Map<string, MedioTransferencia>();
    for (const m of filtrados) {
      const key = (m.mcp ?? '').trim() || `${m.grupo}-${m.id}`;
      if (key && !byMcp.has(key)) byMcp.set(key, m);
    }
    return Array.from(byMcp.values());
  },

  getMcpsPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 100 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: (it?.tipo ?? it?.Tipo ?? '') ? String(it.tipo ?? it.Tipo).trim().toUpperCase() : undefined,
    }));
  },

  getMediosTransferenciaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'TB');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosEfectivoPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'E');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosOtrosPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'O');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosChequesPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase() === 'C');

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupo: async (grupoMcp: number): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: 1, pageSize: 50 }, // Límite del backend para Mcps
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });

    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMediosTarjetaPorGrupoPaged: async (
    grupoMcp: number,
    options: { pageNumber: number; pageSize: number }
  ): Promise<MedioTransferencia[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: { pageNumber: options.pageNumber, pageSize: options.pageSize },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];
    const filtrados = items.filter((it: any) => {
      const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
      const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
      const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
        .trim()
        .toUpperCase();
      const looksLikeTarjeta =
        rawTipo === 'T' ||
        rawTipo === 'TA' ||
        rawTipo === 'TJ' ||
        rawTipo === 'TC' ||
        rawTipo === 'TD' ||
        rawTipo.startsWith('TARJ') ||
        rawClase.includes('TARJ') ||
        rawNombre.includes('TARJ');
      return looksLikeTarjeta;
    });
    return filtrados.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
      nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
      descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
      mcp: it?.mcp ?? it?.MCP ?? undefined,
      clase: it?.clase ?? it?.Clase ?? undefined,
      grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
      nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
      tipo: it?.tipo ?? it?.Tipo ?? undefined,
    }));
  },

  getMarcasTarjetas: async (): Promise<MarcaTarjeta[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/MarcasTarjetas', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      // MarcasTarjetas no tiene paginación, retorna todos los registros
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? idx,
      nombre: it?.nombreMarca ?? it?.nombre ?? it?.descripcion ?? `Marca ${idx + 1}`,
      nombreMarca: it?.nombreMarca,
    }));
  },

  getBancos: async (searchTerm?: string): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: 1,
        pageSize: 20, // Límite del backend
        searchTerm: searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene todos los bancos paginando hasta traerlos completos (sin filtro)
  getBancosAll: async (): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const pageSize = 20; // Límite del backend
    let pageNumber = 1;
    const acumulados: Banco[] = [];

    // Limitar a 100 páginas como salvaguarda
    for (let i = 0; i < 100; i++) {
      const response = await api.get<any>('/api/Banco', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          pageNumber,
          pageSize,
        },
      });
      const data = response.data;
      const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      const mapped: Banco[] = items.map((it: any, idx: number) => ({
        id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
        nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
        codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
        nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
      }));
      acumulados.push(...mapped);
      if (items.length < pageSize) break;
      pageNumber += 1;
    }

    // Eliminar duplicados por id
    const byId = new Map<number, Banco>();
    for (const b of acumulados) {
      if (!byId.has(b.id)) {
        byId.set(b.id, b);
      }
    }
    return Array.from(byId.values());
  },

  // Paginado simple para infinite scroll
  getBancosPaged: async (options: { searchTerm?: string; pageNumber: number; pageSize: number }): Promise<Banco[]> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>('/api/Banco', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        pageNumber: options.pageNumber,
        pageSize: options.pageSize,
        searchTerm: options.searchTerm || undefined,
      },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return items.map((it: any, idx: number) => ({
      id: it?.id ?? it?.codigo ?? it?.codigoBanco ?? it?.nroBanco ?? idx,
      nombre: it?.nombreBanco ?? it?.nombre ?? it?.descripcion ?? `Banco ${idx + 1}`,
      codigo: it?.codigoBanco ?? it?.codigo ?? it?.id,
      nroBanco: it?.nroBanco ?? it?.codigoBanco ?? it?.codigo ?? it?.id,
    }));
  },

  // Obtiene nroSuc asociado al grupo MCP consultando los Mcps del grupo y
  // tomando el primer campo numérico que luzca como sucursal
  getNroSucPorGrupo: async (grupoMcp: number): Promise<number | null> => {
    const token = localStorage.getItem('token');
    const response = await api.get<any>(`/api/Mcps/por-grupo/${grupoMcp}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
      params: { pageNumber: 1, pageSize: 50 },
    });
    const data = response.data;
    const items: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.value)
      ? data.value
      : [];

    const candidateKeys = [
      'nroSuc', 'NroSuc', 'nrosuc', 'sucursal', 'Sucursal', 'nroSucursal', 'nro_suc', 'suc'
    ];

    for (const it of items) {
      for (const key of candidateKeys) {
        if (key in it) {
          const val = Number(it[key]);
          if (Number.isFinite(val) && val > 0) return val;
        }
      }
    }
    return null;
  },

  // Detalle de un MCP por nombre y tipo (E, C, T, TB, O)
  getMcpDetallePorNombreYTipo: async (
    mcpNombre: string,
    tipo: string
  ): Promise<{ grupo?: number; nroSuc?: number; tipo?: string }> => {
    const token = localStorage.getItem('token');
    const encoded = encodeURIComponent(mcpNombre);
    const response = await api.get<any>(`/api/Mcps/${encoded}/${tipo}`, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    const data = response.data || {};
    return {
      grupo: data?.grupoMcp ?? data?.grupo ?? undefined,
      nroSuc: data?.nroSuc ?? data?.sucursal ?? undefined,
      tipo: data?.tipo ?? tipo,
    };
  },

  // Obtener caja abierta por grupo
  getCajaAbiertaPorGrupo: async (numeroGrupo: number): Promise<Caja | null> => {
    const token = localStorage.getItem('token');
    try {
      const response = await api.get<any>('/api/Caja', {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          Estado: 'ABIERTO',
          Grupo: numeroGrupo,
        },
      });
      const data = response.data;
      // La respuesta puede ser un array o un objeto
      let cajas: any[] = [];
      if (Array.isArray(data)) {
        cajas = data;
      } else if (Array.isArray(data?.items)) {
        cajas = data.items;
      } else if (data && !Array.isArray(data)) {
        // Si es un solo objeto, lo convertimos en array
        cajas = [data];
      }
      
      // Retornar la primera caja abierta encontrada
      if (cajas.length > 0) {
        const caja = cajas[0];
        console.log('📋 Caja raw obtenida:', caja);
        const codigoCaja = caja.codigoCaja ?? caja.nroCaja ?? caja.codigo ?? caja.id ?? null;
        console.log('🔢 codigoCaja extraído:', codigoCaja, 'de campos:', {
          codigoCaja: caja.codigoCaja,
          nroCaja: caja.nroCaja,
          codigo: caja.codigo,
          id: caja.id
        });
        return {
          nroCaja: codigoCaja,
          codigo: codigoCaja,
          codigoCaja: codigoCaja,
          grupo: caja.grupo ?? numeroGrupo,
          estado: caja.estado ?? 'ABIERTO',
          ...caja,
        };
      }
      console.log('⚠️ No se encontraron cajas en la respuesta');
      return null;
    } catch (error) {
      console.error('Error al obtener caja abierta:', error);
      return null;
    }
  },

  // Obtener cheques de cartera
  getChequesCartera: async (codigoCaja: number, pageNumber?: number, pageSize?: number): Promise<{ cheques: ChequeCartera[]; hasMore: boolean }> => {
    const token = localStorage.getItem('token');
    console.log('🔍 getChequesCartera - CodigoCaja:', codigoCaja, 'pageNumber:', pageNumber, 'pageSize:', pageSize);
    const response = await api.get<any>('/api/Tesoreria/cheques-cartera', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      params: {
        CodigoCaja: codigoCaja,
        ...(pageNumber ? { pageNumber } : {}),
        ...(pageSize ? { pageSize } : {}),
      },
    });
    console.log('📦 Respuesta completa del endpoint:', response);
    const data = response.data;
    console.log('📋 Data de la respuesta:', data);
    
    // Intentar diferentes estructuras de respuesta
    let cheques: any[] = [];
    if (Array.isArray(data?.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (Array.isArray(data)) {
      cheques = data;
    } else if (data?.chequesCartera && Array.isArray(data.chequesCartera)) {
      cheques = data.chequesCartera;
    } else if (data?.items && Array.isArray(data.items)) {
      cheques = data.items;
    }
    
    // Determinar si hay más páginas
    // Si no se especificó paginación, asumimos que se devolvieron todos
    let hasMore = false;
    if (pageNumber && pageSize) {
      // Si la respuesta tiene exactamente el tamaño de página, probablemente hay más
      if (cheques.length === pageSize) {
        // Intentar usar indicadores del backend si están disponibles
        if (data?.hasMore !== undefined) {
          hasMore = data.hasMore;
        } else if (data?.totalCount !== undefined) {
          hasMore = data.totalCount > (pageNumber * pageSize);
        } else {
          // Si no hay indicadores, asumimos que hay más si devolvió el tamaño completo de página
          hasMore = true;
        }
      } else {
        // Si devolvió menos que el tamaño de página, no hay más
        hasMore = false;
      }
    }
    
    console.log('💳 Cheques procesados:', cheques.length, 'hasMore:', hasMore);
    
    const chequesMapeados = cheques.map((cheque: any) => ({
      nroCarga: cheque.nroCarga ?? 0,
      nroBanco: cheque.nroBanco ?? 0,
      nombreBanco: cheque.nombreBanco ?? '',
      nroSucursal: cheque.nroSucursal ?? 0,
      nombreSucursal: cheque.nombreSucursal ?? '',
      nroCheque: cheque.nroCheque ?? 0,
      nroLibrador: cheque.nroLibrador ?? '',
      librador: cheque.librador ?? '',
      importe: cheque.importe ?? 0,
      fechaIngreso: cheque.fechaIngreso ?? '',
      nroMovCajaIngre: cheque.nroMovCajaIngre ?? 0,
      nroDestino: cheque.nroDestino ?? 0,
      fechaSalida: cheque.fechaSalida ?? '',
      nroMovCajaSalida: cheque.nroMovCajaSalida ?? 0,
      fechaVencimiento: cheque.fechaVencimiento ?? '',
      concepto: cheque.concepto ?? '',
      fechaEmision: cheque.fechaEmision ?? '',
      conciliado: cheque.conciliado ?? 0,
      asentado: cheque.asentado ?? 0,
      mcp: cheque.mcp ?? '',
      nroCuenta: cheque.nroCuenta ?? '',
    }));
    
    return {
      cheques: chequesMapeados,
      hasMore,
    };
  },

  getTransferenciasCaja: async (): Promise<any[]> => {
    const token = localStorage.getItem('token');
    const extractItems = (data: any): any[] =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.value)
        ? data.value
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.items)
        ? data.data.items
        : [];
    const allItems: any[] = [];
    let pageNumber = 1;
    const pageSize = 1000;
    try {
      for (let i = 0; i < 200; i++) {
        const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
          params: { PageNumber: pageNumber, PageSize: pageSize },
        });
        const items = extractItems(response.data);
        if (items.length === 0 && pageNumber === 1) return items;
        if (items.length === 0) break;
        allItems.push(...items);
        if (items.length < pageSize) break;
        pageNumber += 1;
      }
      return allItems;
    } catch {
      try {
        const response = await api.get<any>('/api/TesoreriaCaja/transferencias-caja', {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        return extractItems(response.data);
      } catch {
        return [];
      }
    }
  },

  postTransferenciaCaja: async (payload: any): Promise<any> => {
    const token = localStorage.getItem('token');
    const response = await api.post<any>('/api/TesoreriaCaja/transferencias-caja', payload, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    });
    return response.data;
  },
};

export default tesoreriaService;

